import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatMode } from "@/lib/paymentModes";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtFullDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function DailyCashReportPage() {
  const { selectedSchool, selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";

  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const peso = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const load = useCallback(async () => {
    if (!selectedSchool || !selectedBranch) { setData(null); return; }
    setLoading(true);
    try {
      const { data: res } = await api.get("/accounting/income", {
        params: {
          school_id: selectedSchool.id,
          branch_id: selectedBranch.id,
          date_from: date,
          date_to: date,
        },
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [selectedSchool, selectedBranch, date]);

  useEffect(() => { load(); }, [load]);

  // Merge payments_received + manual_income into one list ordered by time.
  const rows = useMemo(() => {
    if (!data) return [];
    const merged = [
      ...(data.payments_received || []).map((r) => ({
        kind: "payment",
        time: r.created_at || r.date || "",
        student_name: r.student_name || "—",
        category: r.category || "—",
        description: r.description || "",
        amount: Number(r.amount || 0),
        mode: r.mode || "cash",
        reference_no: r.reference_no || "",
        received_by: r.received_by || "—",
      })),
      ...(data.manual_income || []).map((r) => ({
        kind: "manual",
        time: r.created_at || r.date || "",
        student_name: "Manual Income",
        category: "Manual",
        description: r.description || "",
        amount: Number(r.amount || 0),
        mode: r.mode || "cash",
        reference_no: r.reference_no || "",
        received_by: r.received_by || "—",
      })),
    ];
    merged.sort((a, b) => String(a.time).localeCompare(String(b.time)));
    return merged;
  }, [data]);

  // Per-mode breakdown.
  const modeTotals = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      acc[r.mode] = (acc[r.mode] || 0) + r.amount;
    }
    return Object.entries(acc).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  // Per-cashier breakdown.
  const cashierTotals = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      const key = r.received_by || "—";
      acc[key] = (acc[key] || 0) + r.amount;
    }
    return Object.entries(acc).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const grandTotal = useMemo(() => rows.reduce((a, b) => a + b.amount, 0), [rows]);

  if (!selectedSchool) {
    return (
      <>
        <NoSchoolCard />
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Controls (hidden when printing) */}
        <div className="no-print">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">Daily Cash Report</h1>
              <p className="text-sm text-brand-inkMuted mt-1">Printable end-of-day audit summary for one branch.</p>
            </div>
            <Button onClick={() => window.print()} disabled={rows.length === 0} data-testid="dcr-print-btn" className="rounded-2xl bg-brand-purple text-white">
              <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
            </Button>
          </div>

          <div className="mt-5 grid sm:grid-cols-3 gap-4 bg-white rounded-3xl border border-brand-purple/10 p-5">
            <div>
              <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Branch</Label>
              <select
                value={selectedBranch?.id || ""}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
                data-testid="dcr-branch-select"
              >
                {branchesForSelectedSchool.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </select>
            </div>
            <div>
              <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Report Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 rounded-2xl h-11" data-testid="dcr-date" />
            </div>
            <div className="flex items-end">
              <div className="rounded-2xl bg-[#F6F8FE] px-4 py-2.5 w-full">
                <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">Total Collected</p>
                <p className="font-heading text-2xl font-bold text-brand-purple tabular-nums">{peso(grandTotal)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Printable area */}
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-8 print-area" data-testid="dcr-report-card">
          <div className="border-b border-brand-purple/10 pb-5 mb-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-brand-inkMuted font-heading">Daily Cash Report</p>
                <h2 className="font-heading text-2xl font-bold text-foreground mt-1">{selectedSchool?.name || "—"}</h2>
                <p className="text-sm text-brand-inkMuted mt-0.5">Branch: <span className="font-medium text-foreground">{selectedBranch?.name || "—"}</span></p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-brand-inkMuted font-heading">Report Date</p>
                <p className="font-heading text-lg font-semibold text-foreground">{fmtFullDate(date)}</p>
                <p className="text-[11px] text-brand-inkMuted mt-1">Generated {new Date().toLocaleString()}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <p className="text-center text-brand-inkMuted py-10">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-brand-inkMuted/40 mx-auto mb-3" />
              <p className="text-brand-inkMuted">No payments recorded for this date.</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm dcr-table" data-testid="dcr-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading border-b border-brand-purple/15">
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Student / Source</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Description</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Ref No.</th>
                    <th className="py-2">Received By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-brand-purple/5">
                      <td className="py-2 pr-3 tabular-nums text-brand-inkMuted">{i + 1}</td>
                      <td className="py-2 pr-3 tabular-nums text-brand-inkMuted whitespace-nowrap">{r.time ? new Date(r.time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                      <td className="py-2 pr-3 font-medium">{r.student_name}</td>
                      <td className="py-2 pr-3">{r.category}</td>
                      <td className="py-2 pr-3 text-brand-inkMuted">{r.description || "—"}</td>
                      <td className="py-2 pr-3 text-right font-semibold tabular-nums">{peso(r.amount)}</td>
                      <td className="py-2 pr-3">{formatMode(r.mode)}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{r.reference_no || "—"}</td>
                      <td className="py-2">{r.received_by}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-brand-purple/30">
                    <td colSpan={5} className="py-3 pr-3 text-right font-heading font-semibold uppercase tracking-wider text-xs text-brand-inkMuted">Grand Total</td>
                    <td className="py-3 pr-3 text-right font-heading text-lg font-bold text-brand-purple tabular-nums">{peso(grandTotal)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>

              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <BreakdownCard title="By Mode of Payment" rows={modeTotals.map(([k, v]) => ({ label: formatMode(k), value: v }))} grand={grandTotal} peso={peso} testid="dcr-mode-breakdown" />
                <BreakdownCard title="By Cashier" rows={cashierTotals.map(([k, v]) => ({ label: k, value: v }))} grand={grandTotal} peso={peso} testid="dcr-cashier-breakdown" />
              </div>

              <div className="mt-10 pt-6 border-t border-brand-purple/10 grid grid-cols-2 gap-10 dcr-signoff">
                <SignBlock label="Prepared by" />
                <SignBlock label="Verified by" />
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          aside, header, .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-area { border: none !important; box-shadow: none !important; padding: 0 !important; border-radius: 0 !important; }
          .dcr-table { font-size: 11px; }
          .dcr-table th, .dcr-table td { padding-top: 4px !important; padding-bottom: 4px !important; }
          .dcr-signoff { page-break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>
    </>
  );
}

function BreakdownCard({ title, rows, grand, peso, testid }) {
  return (
    <div className="rounded-2xl border border-brand-purple/10 bg-[#F6F8FE]/40 p-5" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading mb-3">{title}</p>
      <ul className="space-y-2">
        {rows.length === 0 ? <li className="text-sm text-brand-inkMuted">—</li> : rows.map((r, i) => {
          const pct = grand > 0 ? (r.value / grand) * 100 : 0;
          return (
            <li key={i} className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-foreground truncate">{r.label}</span>
              <span className="flex items-center gap-2 tabular-nums">
                <span className="text-[11px] text-brand-inkMuted">{pct.toFixed(1)}%</span>
                <span className="font-heading font-semibold text-foreground">{peso(r.value)}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SignBlock({ label }) {
  return (
    <div>
      <div className="h-12 border-b border-foreground/40"></div>
      <p className="text-xs text-brand-inkMuted mt-2 font-heading uppercase tracking-wider">{label}</p>
    </div>
  );
}

function NoSchoolCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3"><AlertCircle className="w-7 h-7 text-[#E65100]" /></div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a school first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">The Daily Cash Report is scoped per branch.</p>
    </div>
  );
}
